import { z } from "zod";
import { ConfidenceSchema, IdSchema, NonEmptyStringSchema, NonNegativeIntegerSchema, UnknownRecordSchema } from "./source.js";
/**
 * The app-side boundary for retained titan-engines artifacts.
 *
 * Input values are deliberately inspected as unknown JSON. The translators
 * below read only the fields they own and construct strict app schemas; extra
 * engine fields are therefore ignored rather than allowed to leak engine
 * objects into the app.
 */
export const ENGINE_ARTIFACT_ADAPTER_SCHEMA_VERSION = "engine-artifact-adapter.v1";
const Sha256Schema = z.string().regex(/^[a-f0-9]{64}$/i);
const JsonScalarSchema = z.union([
    z.string(),
    z.number().finite(),
    z.boolean(),
    z.null()
]);
const EngineValueSchema = z.union([JsonScalarSchema, z.array(JsonScalarSchema)]);
export const EngineRefSchema = z
    .object({
    tag: NonEmptyStringSchema,
    commit: z.string().regex(/^[a-f0-9]{7,40}$/i)
})
    .strict();
export const EngineArtifactReferenceSchema = z
    .object({
    artifactPath: NonEmptyStringSchema,
    sha256: Sha256Schema,
    role: NonEmptyStringSchema.optional(),
    documentClass: NonEmptyStringSchema.optional()
})
    .strict();
export const EngineChainEdgeSchema = z
    .object({
    fromStage: NonEmptyStringSchema,
    toStage: NonEmptyStringSchema,
    artifactPath: NonEmptyStringSchema,
    sha256: Sha256Schema,
    verifiedAtRuntime: z.boolean()
})
    .strict();
export const EngineArtifactProvenanceSchema = z
    .object({
    runId: IdSchema,
    artifactPath: NonEmptyStringSchema,
    sha256: Sha256Schema,
    engineRef: EngineRefSchema,
    stageId: NonEmptyStringSchema,
    inputArtifacts: z.array(EngineArtifactReferenceSchema),
    chainEdges: z.array(EngineChainEdgeSchema)
})
    .strict();
export const EngineHonestStatusCategorySchema = z.enum([
    "success",
    "failure",
    "escalated",
    "draft",
    "unable-to-check",
    "unknown"
]);
export const EngineHonestStatusSchema = z
    .object({
    raw: NonEmptyStringSchema,
    category: EngineHonestStatusCategorySchema
})
    .strict();
const EngineConfidenceSchema = z.union([ConfidenceSchema, NonEmptyStringSchema]);
export const EngineUsageSchema = z
    .object({
    inputTokens: NonNegativeIntegerSchema,
    outputTokens: NonNegativeIntegerSchema
})
    .strict();
const EngineMeterFailureSchema = z.union([NonEmptyStringSchema, UnknownRecordSchema]);
export const EngineProviderMeterSchema = z
    .object({
    kind: z.literal("provider-meter"),
    status: EngineHonestStatusSchema,
    usage: EngineUsageSchema,
    completedCalls: NonNegativeIntegerSchema,
    failures: z.array(EngineMeterFailureSchema),
    provenance: EngineArtifactProvenanceSchema
})
    .strict();
const EngineEvidenceTraceSchema = z
    .object({
    sourceRef: NonEmptyStringSchema,
    operation: NonEmptyStringSchema,
    outcome: NonEmptyStringSchema,
    detail: NonEmptyStringSchema
})
    .strict();
export const EngineQuantitySchema = z
    .object({
    value: EngineValueSchema,
    unit: NonEmptyStringSchema,
    granularity: NonEmptyStringSchema,
    status: NonEmptyStringSchema,
    sourceKind: NonEmptyStringSchema,
    sourceRefs: z.array(NonEmptyStringSchema),
    rawLabels: z.array(NonEmptyStringSchema),
    notes: z.array(NonEmptyStringSchema),
    evidenceTrace: z.array(EngineEvidenceTraceSchema)
})
    .strict();
export const EngineScopeFieldCandidateSchema = z
    .object({
    seat: NonEmptyStringSchema,
    quantity: EngineQuantitySchema
})
    .strict();
export const EngineScopeFieldSchema = z
    .object({
    field: NonEmptyStringSchema,
    selected: EngineQuantitySchema,
    candidates: z.array(EngineScopeFieldCandidateSchema),
    conflict: z.boolean()
})
    .strict();
export const EngineRoomSchema = z
    .object({
    rawName: NonEmptyStringSchema,
    normalizedType: NonEmptyStringSchema,
    count: NonNegativeIntegerSchema
})
    .strict();
export const EngineMeterRecordSchema = z
    .object({
    id: NonEmptyStringSchema,
    interface: NonEmptyStringSchema.optional(),
    kind: NonEmptyStringSchema.optional(),
    ordinal: NonNegativeIntegerSchema,
    protocolRef: NonEmptyStringSchema.optional(),
    roomRef: NonEmptyStringSchema.optional(),
    sourceRefs: z.array(NonEmptyStringSchema),
    status: NonEmptyStringSchema
})
    .strict();
export const EngineScopeSchema = z
    .object({
    scopeId: IdSchema,
    homeTypeId: IdSchema,
    fields: z.array(EngineScopeFieldSchema),
    rooms: z.array(EngineRoomSchema),
    meters: z.array(EngineMeterRecordSchema)
})
    .strict();
export const EngineTakeoffGapSchema = z
    .object({
    field: NonEmptyStringSchema,
    reason: NonEmptyStringSchema,
    requestedSource: NonEmptyStringSchema.optional()
})
    .strict();
const EngineRunSummarySchema = z
    .object({
    provider: NonEmptyStringSchema,
    model: NonEmptyStringSchema,
    providerCalls: NonNegativeIntegerSchema,
    usage: EngineUsageSchema
})
    .strict();
export const EngineTakeoffTranslationSchema = z
    .object({
    kind: z.literal("takeoff"),
    engineRunId: IdSchema,
    status: EngineHonestStatusSchema,
    certificationStatus: z.enum(["certified", "not-certified"]),
    schemaVersion: NonEmptyStringSchema,
    scope: EngineScopeSchema,
    gaps: z.array(EngineTakeoffGapSchema),
    reconciliationWarnings: z.array(NonEmptyStringSchema),
    run: EngineRunSummarySchema,
    provenance: EngineArtifactProvenanceSchema
})
    .strict();
export const EngineTakeoffFamilyTranslationSchema = z
    .object({
    kind: z.literal("takeoff-family"),
    family: NonEmptyStringSchema,
    status: EngineHonestStatusSchema,
    certificationStatus: z.enum(["certified", "not-certified"]),
    scope: EngineScopeSchema,
    provenance: EngineArtifactProvenanceSchema
})
    .strict();
export const EngineProjectionTranslationSchema = z
    .object({
    kind: z.literal("projection"),
    status: EngineHonestStatusSchema,
    certificationStatus: z.enum(["certified", "not-certified"]),
    schemaVersion: NonEmptyStringSchema,
    selectedType: IdSchema,
    scope: EngineScopeSchema,
    gaps: z.array(EngineTakeoffGapSchema),
    reconciliationWarnings: z.array(NonEmptyStringSchema),
    sourceArtifact: EngineArtifactReferenceSchema,
    scoreArtifact: EngineArtifactReferenceSchema.optional(),
    provenance: EngineArtifactProvenanceSchema
})
    .strict();
export const EngineManifestStageSchema = z
    .object({
    ordinal: NonNegativeIntegerSchema,
    stageId: NonEmptyStringSchema,
    status: EngineHonestStatusSchema,
    stageRunId: IdSchema.optional(),
    provider: NonEmptyStringSchema.optional(),
    model: NonEmptyStringSchema.optional(),
    usage: EngineUsageSchema.optional(),
    inputArtifacts: z.array(EngineArtifactReferenceSchema),
    outputArtifacts: z.array(EngineArtifactReferenceSchema)
})
    .strict();
export const EngineManifestTotalsSchema = z
    .object({
    inputTokens: NonNegativeIntegerSchema,
    outputTokens: NonNegativeIntegerSchema,
    gbp: z.number().nonnegative()
})
    .strict();
export const EngineProviderMeterSummarySchema = z
    .object({
    usage: EngineUsageSchema,
    completedCalls: NonNegativeIntegerSchema.optional(),
    failures: z.array(EngineMeterFailureSchema),
    source: z.enum(["stage-owned-provider-meter", "unavailable"])
})
    .strict();
export const EngineManifestTranslationSchema = z
    .object({
    kind: z.literal("chain-manifest"),
    pipelineRunId: IdSchema,
    gitSha: z.string().regex(/^[a-f0-9]{7,40}$/i),
    gitDirty: z.boolean(),
    totalsIncomplete: z.boolean(),
    apartmentType: IdSchema,
    schemaVersion: NonEmptyStringSchema,
    status: EngineHonestStatusSchema,
    chainEdges: z.array(EngineChainEdgeSchema),
    stages: z.array(EngineManifestStageSchema),
    manifestTotals: EngineManifestTotalsSchema,
    providerMeter: EngineProviderMeterSummarySchema,
    provenance: EngineArtifactProvenanceSchema
})
    .strict();
export const EnginePointFrequencySchema = z
    .object({
    presentInCorpora: NonNegativeIntegerSchema,
    evaluatedCorpora: NonNegativeIntegerSchema,
    corpora: z.array(NonEmptyStringSchema),
    interpretation: NonEmptyStringSchema
})
    .strict();
export const EnginePointLoadSchema = z
    .object({
    status: NonEmptyStringSchema,
    value: z.number().finite().nullable(),
    unit: NonEmptyStringSchema.nullable(),
    relayRequired: NonEmptyStringSchema.nullable()
})
    .strict();
export const EngineConfirmationFlagSchema = z
    .object({
    id: NonEmptyStringSchema,
    text: NonEmptyStringSchema,
    materiality: NonEmptyStringSchema
})
    .strict();
export const EnginePointProvenanceSchema = z
    .object({
    basis: NonEmptyStringSchema,
    requirementRefs: z.array(NonEmptyStringSchema),
    sourceRefs: z.array(NonEmptyStringSchema),
    assumptionTrigger: NonEmptyStringSchema.optional(),
    confidence: EngineConfidenceSchema.optional(),
    frequency: EnginePointFrequencySchema.optional()
})
    .strict();
export const EnginePointSchema = z
    .object({
    id: IdSchema,
    equipmentId: NonEmptyStringSchema,
    equipmentKind: NonEmptyStringSchema,
    zone: NonEmptyStringSchema,
    function: NonEmptyStringSchema,
    type: NonEmptyStringSchema,
    state: NonEmptyStringSchema,
    trigger: NonEmptyStringSchema,
    contractClass: NonEmptyStringSchema,
    safetyRole: NonEmptyStringSchema,
    exclusions: z.array(NonEmptyStringSchema),
    confidence: EngineConfidenceSchema.optional(),
    frequency: EnginePointFrequencySchema.optional(),
    powerSource: NonEmptyStringSchema,
    load: EnginePointLoadSchema,
    requirementRefs: z.array(NonEmptyStringSchema),
    safetyPatternRefs: z.array(NonEmptyStringSchema),
    sourceRefs: z.array(NonEmptyStringSchema),
    reasons: z.array(NonEmptyStringSchema),
    confirmationFlags: z.array(EngineConfirmationFlagSchema),
    needsRich: z.boolean(),
    provenance: EnginePointProvenanceSchema
})
    .strict();
export const EnginePointTotalsSchema = z
    .object({
    byType: z.record(z.string(), NonNegativeIntegerSchema),
    byState: z.record(z.string(), NonNegativeIntegerSchema),
    total: NonNegativeIntegerSchema
})
    .strict();
export const EnginePointScheduleSchema = z
    .object({
    points: z.array(EnginePointSchema),
    totals: EnginePointTotalsSchema
})
    .strict();
export const EngineAllocationSchema = z
    .object({
    pointId: IdSchema,
    placementId: IdSchema,
    productCode: NonEmptyStringSchema,
    capacityKind: NonEmptyStringSchema.optional(),
    logicOwnerPlacementId: IdSchema.optional()
})
    .strict();
export const EnginePlacementSchema = z
    .object({
    placementId: IdSchema,
    productCode: NonEmptyStringSchema,
    placementClass: NonEmptyStringSchema,
    purpose: NonEmptyStringSchema,
    location: NonEmptyStringSchema
})
    .strict();
export const EnginePointCoverageSchema = z
    .object({
    pointId: IdSchema,
    disposition: NonEmptyStringSchema,
    placementId: IdSchema.optional(),
    note: NonEmptyStringSchema.optional()
})
    .strict();
export const EngineAllocationSummarySchema = z
    .object({
    deviceAllocations: z.array(EngineAllocationSchema),
    ioAllocations: z.array(EngineAllocationSchema),
    placements: z.array(EnginePlacementSchema),
    pointCoverage: z.array(EnginePointCoverageSchema),
    seriesSwitchingAllocations: z.array(EngineAllocationSchema)
})
    .strict();
export const EnginePointsC1Schema = z
    .object({
    status: EngineHonestStatusSchema,
    receiptStatus: NonEmptyStringSchema,
    allocationValid: z.boolean(),
    allocationValiditySource: z.enum([
        "receipt.allocationValid",
        "c1.allocationValid",
        "receipt.toolReceipts.calculateTotals"
    ]),
    toolReceiptsValid: z.boolean().optional(),
    refireExhausted: z.boolean(),
    refireLimit: NonNegativeIntegerSchema,
    selectionRefireExhausted: z.boolean().optional(),
    allocationRefireCount: NonNegativeIntegerSchema,
    selectionRefireCount: NonNegativeIntegerSchema,
    failures: z.array(NonEmptyStringSchema),
    failuresByPhase: z.record(z.string(), z.array(NonEmptyStringSchema)),
    finalAllocations: EngineAllocationSummarySchema
})
    .strict();
export const EnginePointsTranslationSchema = z
    .object({
    kind: z.literal("points"),
    status: EngineHonestStatusSchema,
    certificationStatus: z.enum(["certified", "not-certified"]),
    sourceRunId: IdSchema,
    inputMode: NonEmptyStringSchema,
    inputArtifacts: z.array(EngineArtifactReferenceSchema),
    schedules: z
        .object({
        base: EnginePointScheduleSchema.optional(),
        afterP2: EnginePointScheduleSchema.optional(),
        points: EnginePointScheduleSchema
    })
        .strict(),
    c1: EnginePointsC1Schema,
    provenance: EngineArtifactProvenanceSchema
})
    .strict();
export const EngineCatalogGapSchema = z
    .object({
    gapId: IdSchema,
    sku: NonEmptyStringSchema,
    reason: NonEmptyStringSchema,
    requiredProvision: NonEmptyStringSchema
})
    .strict();
export const EngineFeedbackFlagSchema = z
    .object({
    flagId: IdSchema,
    kind: NonEmptyStringSchema,
    status: NonEmptyStringSchema,
    statement: NonEmptyStringSchema,
    affectedRefs: z.array(NonEmptyStringSchema),
    basis: NonEmptyStringSchema,
    nextAction: NonEmptyStringSchema
})
    .strict();
export const EnginePanelFitSchema = z
    .object({
    status: EngineHonestStatusSchema,
    calculationRef: NonEmptyStringSchema,
    knownComponentRefs: z.array(NonEmptyStringSchema),
    knownRequiredDinWidthMm: z.number().nonnegative().optional(),
    knownMaximumComponentHeightMm: z.number().nonnegative().optional(),
    knownMaximumComponentDepthMm: z.number().nonnegative().optional(),
    reason: NonEmptyStringSchema
})
    .strict();
export const EnginePanelSpareCapacitySchema = z
    .object({
    status: EngineHonestStatusSchema,
    requiredPercent: z.number().nonnegative(),
    actualPercent: z.number().nonnegative().nullable(),
    policyProfileRef: NonEmptyStringSchema,
    calculationRef: NonEmptyStringSchema,
    reasoning: NonEmptyStringSchema
})
    .strict();
export const EnginePanelPsuCapacitySchema = z
    .object({
    status: EngineHonestStatusSchema,
    calculationRef: NonEmptyStringSchema,
    unit: NonEmptyStringSchema,
    totalCapacity: z.number().finite(),
    totalDraw: z.number().finite(),
    requiredCapacity: z.number().finite(),
    reservePercent: z.number().finite(),
    overloaded: z.boolean().nullable(),
    missingConsumerRefs: z.array(NonEmptyStringSchema),
    excludedConsumerRefs: z.array(NonEmptyStringSchema),
    assumptionComponentRefs: z.array(NonEmptyStringSchema),
    reasoning: NonEmptyStringSchema
})
    .strict();
export const EnginePanelRelayLoadingSchema = z
    .object({
    status: EngineHonestStatusSchema,
    calculationRef: NonEmptyStringSchema,
    totalConnectedLoadA: z.number().finite(),
    overloaded: z.boolean().nullable(),
    unknownLoadPointIds: z.array(NonEmptyStringSchema),
    assumedLoadPointIds: z.array(NonEmptyStringSchema)
})
    .strict();
export const EnginePanelComponentSchema = z
    .object({
    componentId: IdSchema,
    sku: NonEmptyStringSchema,
    quantity: NonNegativeIntegerSchema,
    reasoning: NonEmptyStringSchema,
    sourceRefs: z.array(NonEmptyStringSchema)
})
    .strict();
export const EnginePanelSchema = z
    .object({
    panelId: IdSchema,
    kind: NonEmptyStringSchema,
    location: NonEmptyStringSchema,
    fit: EnginePanelFitSchema,
    spareCapacity: EnginePanelSpareCapacitySchema,
    psuCapacity: EnginePanelPsuCapacitySchema.optional(),
    relayLoading: EnginePanelRelayLoadingSchema.optional(),
    controllers: z.array(EnginePanelComponentSchema),
    ioModules: z.array(EnginePanelComponentSchema),
    netmasters: z.array(EnginePanelComponentSchema),
    screens: z.array(EnginePanelComponentSchema),
    psus: z.array(EnginePanelComponentSchema),
    relays: z.array(EnginePanelComponentSchema)
})
    .strict();
export const EngineUnknownSchema = z
    .object({
    unknownId: IdSchema,
    category: NonEmptyStringSchema,
    statement: NonEmptyStringSchema,
    downstreamEffect: NonEmptyStringSchema,
    affectedRefs: z.array(NonEmptyStringSchema),
    sourceRefs: z.array(NonEmptyStringSchema)
})
    .strict();
export const EnginePanelTranslationSchema = z
    .object({
    kind: z.literal("panel"),
    status: EngineHonestStatusSchema,
    artifactKind: NonEmptyStringSchema,
    schemaVersion: NonEmptyStringSchema,
    catalogGaps: z.array(EngineCatalogGapSchema),
    feedbackFlags: z.array(EngineFeedbackFlagSchema),
    unknowns: z.array(EngineUnknownSchema),
    panels: z.array(EnginePanelSchema),
    provenance: EngineArtifactProvenanceSchema
})
    .strict();
export const EngineScoreSummarySchema = z
    .object({
    correct: NonNegativeIntegerSchema,
    wrong: NonNegativeIntegerSchema,
    missing: NonNegativeIntegerSchema,
    fabricated: NonNegativeIntegerSchema,
    truthGap: NonNegativeIntegerSchema,
    scored: NonNegativeIntegerSchema,
    total: NonNegativeIntegerSchema
})
    .strict();
export const EngineFamilyScoreSchema = z
    .object({
    family: NonEmptyStringSchema,
    text: EngineScoreSummarySchema,
    vision: EngineScoreSummarySchema,
    reconciled: EngineScoreSummarySchema
})
    .strict();
export const EngineFutureTruthProvenanceSchema = z
    .object({
    sheet: NonEmptyStringSchema,
    row: NonNegativeIntegerSchema,
    columns: z.array(NonEmptyStringSchema),
    method: NonEmptyStringSchema,
    formula: NonEmptyStringSchema.nullable()
})
    .strict();
export const EngineFutureTruthFieldSchema = z
    .object({
    field: NonEmptyStringSchema,
    value: z.number().finite(),
    provenance: EngineFutureTruthProvenanceSchema
})
    .strict();
export const EngineScorecardTranslationSchema = z
    .object({
    kind: z.literal("scorecard"),
    engineRunId: IdSchema,
    status: EngineHonestStatusSchema,
    provider: NonEmptyStringSchema,
    model: NonEmptyStringSchema,
    homeTypeId: IdSchema,
    truthClass: NonEmptyStringSchema,
    reconciled: EngineScoreSummarySchema,
    familyScores: z.array(EngineFamilyScoreSchema),
    futureTruth: z.array(EngineFutureTruthFieldSchema),
    reconciliationWarnings: z.array(NonEmptyStringSchema),
    provenance: EngineArtifactProvenanceSchema
})
    .strict();
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function requiredRecord(value, path) {
    if (!isRecord(value)) {
        throw new Error(`Missing required engine artifact object at ${path}.`);
    }
    return value;
}
function requiredChild(record, key, path) {
    if (!(key in record) || record[key] === undefined || record[key] === null) {
        throw new Error(`Missing required engine artifact field ${path}.${key}.`);
    }
    return record[key];
}
function requiredString(record, key, path) {
    const value = requiredChild(record, key, path);
    if (typeof value !== "string" || value.length === 0) {
        throw new Error(`Invalid required engine artifact string at ${path}.${key}.`);
    }
    return value;
}
function requiredBoolean(record, key, path) {
    const value = requiredChild(record, key, path);
    if (typeof value !== "boolean") {
        throw new Error(`Invalid required engine artifact boolean at ${path}.${key}.`);
    }
    return value;
}
function requiredNumber(record, key, path) {
    const value = requiredChild(record, key, path);
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new Error(`Invalid required engine artifact number at ${path}.${key}.`);
    }
    return value;
}
function requiredNonNegativeInteger(record, key, path) {
    const value = requiredNumber(record, key, path);
    if (!Number.isInteger(value) || value < 0) {
        throw new Error(`Invalid required engine artifact non-negative integer at ${path}.${key}.`);
    }
    return value;
}
function requiredArray(record, key, path) {
    const value = requiredChild(record, key, path);
    if (!Array.isArray(value)) {
        throw new Error(`Invalid required engine artifact array at ${path}.${key}.`);
    }
    return value;
}
function optionalString(record, key, path) {
    const value = record[key];
    if (value === undefined || value === null)
        return undefined;
    if (typeof value !== "string" || value.length === 0) {
        throw new Error(`Invalid optional engine artifact string at ${path}.${key}.`);
    }
    return value;
}
function optionalBoolean(record, key, path) {
    const value = record[key];
    if (value === undefined || value === null)
        return undefined;
    if (typeof value !== "boolean") {
        throw new Error(`Invalid optional engine artifact boolean at ${path}.${key}.`);
    }
    return value;
}
function optionalNumber(record, key, path) {
    const value = record[key];
    if (value === undefined || value === null)
        return undefined;
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new Error(`Invalid optional engine artifact number at ${path}.${key}.`);
    }
    return value;
}
function requiredStringArray(record, key, path) {
    return requiredArray(record, key, path).map((value, index) => {
        if (typeof value !== "string" || value.length === 0) {
            throw new Error(`Invalid engine artifact string at ${path}.${key}[${index}].`);
        }
        return value;
    });
}
function optionalStringArray(record, key, path) {
    if (record[key] === undefined || record[key] === null)
        return [];
    return requiredStringArray(record, key, path);
}
function requiredSha(record, key, path) {
    const value = requiredString(record, key, path);
    if (!/^[a-f0-9]{64}$/i.test(value)) {
        throw new Error(`Invalid SHA-256 at ${path}.${key}.`);
    }
    return value;
}
function status(raw) {
    let category = "unknown";
    if (["PASS", "SUCCEEDED", "succeeded", "completed", "executed"].includes(raw)) {
        category = "success";
    }
    else if (["FAIL", "FAILED", "failed", "BLOCKED"].includes(raw)) {
        category = "failure";
    }
    else if (raw === "ESCALATE") {
        category = "escalated";
    }
    else if (raw === "DRAFT") {
        category = "draft";
    }
    else if (raw === "unable-to-check") {
        category = "unable-to-check";
    }
    return { raw, category };
}
function statusFromArtifact(record, context, fallback = "unknown") {
    // A draft upstream boundary is authoritative for downstream certification:
    // a later artifact cannot silently turn an un-certified chain into a
    // certified result.
    if (context.status)
        return status(context.status);
    for (const key of [
        "status",
        "verdict",
        "certification_status",
        "certificationStatus"
    ]) {
        const value = record[key];
        if (typeof value === "string" && value.trim()) {
            return status(value);
        }
    }
    return status(fallback);
}
function certificationStatusFor(statusValue) {
    return statusValue.category === "success" ? "certified" : "not-certified";
}
function deriveManifestStatus(stages) {
    const meaningfulCategories = [
        "failure",
        "escalated",
        "draft",
        "unable-to-check"
    ];
    for (const category of meaningfulCategories) {
        const stage = stages.find((candidate) => candidate.status.category === category);
        if (stage)
            return stage.status;
    }
    return status("unknown");
}
function contextProvenance(context) {
    return EngineArtifactProvenanceSchema.parse({
        ...context.provenance,
        inputArtifacts: context.provenance.inputArtifacts ?? [],
        chainEdges: context.provenance.chainEdges ?? []
    });
}
function valueAt(record, key, path) {
    const value = requiredChild(record, key, path);
    const parsed = EngineValueSchema.safeParse(value);
    if (!parsed.success) {
        throw new Error(`Invalid engine artifact value at ${path}.${key}.`);
    }
    return parsed.data;
}
function mapEvidenceTrace(value, path) {
    if (value === undefined || value === null)
        return [];
    if (!Array.isArray(value)) {
        throw new Error(`Invalid engine artifact evidenceTrace at ${path}.`);
    }
    return value.map((item, index) => {
        const record = requiredRecord(item, `${path}[${index}]`);
        return {
            sourceRef: requiredString(record, "sourceRef", `${path}[${index}]`),
            operation: requiredString(record, "operation", `${path}[${index}]`),
            outcome: requiredString(record, "outcome", `${path}[${index}]`),
            detail: requiredString(record, "detail", `${path}[${index}]`)
        };
    });
}
function mapQuantity(value, path) {
    const record = requiredRecord(value, path);
    return EngineQuantitySchema.parse({
        // The retained take-off omits `value` on not-found candidate quantities;
        // null is the app-side representation of that honest absence.
        value: record.value === undefined || record.value === null
            ? null
            : valueAt(record, "value", path),
        unit: requiredString(record, "unit", path),
        granularity: requiredString(record, "granularity", path),
        status: requiredString(record, "status", path),
        sourceKind: requiredString(record, "sourceKind", path),
        sourceRefs: requiredStringArray(record, "sourceRefs", path),
        rawLabels: optionalStringArray(record, "rawLabels", path),
        notes: optionalStringArray(record, "notes", path),
        evidenceTrace: mapEvidenceTrace(record.evidenceTrace, `${path}.evidenceTrace`)
    });
}
function mapScope(value, path) {
    const scope = requiredRecord(value, path);
    const fieldsValue = requiredChild(scope, "fields", path);
    const fieldsEntries = Array.isArray(fieldsValue)
        ? fieldsValue.map((value, index) => {
            const field = requiredRecord(value, `${path}.fields[${index}]`);
            return [requiredString(field, "field", `${path}.fields[${index}]`), field];
        })
        : Object.entries(requiredRecord(fieldsValue, `${path}.fields`));
    const fields = fieldsEntries.map(([key, value]) => {
        const field = requiredRecord(value, `${path}.fields.${key}`);
        const candidates = field.candidates === undefined || field.candidates === null
            ? []
            : requiredArray(field, "candidates", `${path}.fields.${key}`).map((candidate, index) => {
                const candidateRecord = requiredRecord(candidate, `${path}.fields.${key}.candidates[${index}]`);
                return {
                    seat: requiredString(candidateRecord, "seat", `${path}.fields.${key}.candidates[${index}]`),
                    quantity: mapQuantity(requiredChild(candidateRecord, "quantity", `${path}.fields.${key}.candidates[${index}]`), `${path}.fields.${key}.candidates[${index}].quantity`)
                };
            });
        return {
            field: optionalString(field, "field", `${path}.fields.${key}`) ?? key,
            selected: mapQuantity(requiredChild(field, "selected", `${path}.fields.${key}`), `${path}.fields.${key}.selected`),
            candidates,
            conflict: optionalBoolean(field, "conflict", `${path}.fields.${key}`) ?? false
        };
    });
    const rooms = scope.rooms === undefined || scope.rooms === null
        ? []
        : requiredArray(scope, "rooms", path).map((value, index) => {
            const room = requiredRecord(value, `${path}.rooms[${index}]`);
            return {
                rawName: requiredString(room, "rawName", `${path}.rooms[${index}]`),
                normalizedType: requiredString(room, "normalizedType", `${path}.rooms[${index}]`),
                count: requiredNonNegativeInteger(room, "count", `${path}.rooms[${index}]`)
            };
        });
    const meters = scope.meters === undefined || scope.meters === null
        ? []
        : requiredArray(scope, "meters", path).map((value, index) => {
            const meter = requiredRecord(value, `${path}.meters[${index}]`);
            return {
                id: requiredString(meter, "id", `${path}.meters[${index}]`),
                interface: optionalString(meter, "interface", `${path}.meters[${index}]`),
                kind: optionalString(meter, "kind", `${path}.meters[${index}]`),
                ordinal: requiredNonNegativeInteger(meter, "ordinal", `${path}.meters[${index}]`),
                protocolRef: optionalString(meter, "protocolRef", `${path}.meters[${index}]`),
                roomRef: optionalString(meter, "roomRef", `${path}.meters[${index}]`),
                sourceRefs: requiredStringArray(meter, "sourceRefs", `${path}.meters[${index}]`),
                status: requiredString(meter, "status", `${path}.meters[${index}]`)
            };
        });
    return EngineScopeSchema.parse({
        scopeId: requiredString(scope, "scopeId", path),
        homeTypeId: requiredString(scope, "homeTypeId", path),
        fields,
        rooms,
        meters
    });
}
function mapGaps(value, path) {
    return requiredArray(requiredRecord({ value }, "root"), "value", "root").map((item, index) => {
        const gap = requiredRecord(item, `${path}[${index}]`);
        return {
            field: requiredString(gap, "field", `${path}[${index}]`),
            reason: requiredString(gap, "reason", `${path}[${index}]`),
            requestedSource: optionalString(gap, "requestedSource", `${path}[${index}]`)
        };
    });
}
function mapArtifactReference(value, path, role) {
    const record = requiredRecord(value, path);
    return EngineArtifactReferenceSchema.parse({
        artifactPath: requiredString(record, "path", path),
        sha256: requiredSha(record, "sha256", path),
        role: role ?? optionalString(record, "role", path),
        documentClass: optionalString(record, "documentClass", path)
    });
}
function mapChainEdges(value, path) {
    if (!Array.isArray(value)) {
        throw new Error(`Missing required engine artifact chain array at ${path}.`);
    }
    return value.map((item, index) => {
        const edge = requiredRecord(item, `${path}[${index}]`);
        return EngineChainEdgeSchema.parse({
            fromStage: requiredString(edge, "from", `${path}[${index}]`),
            toStage: requiredString(edge, "to", `${path}[${index}]`),
            artifactPath: requiredString(edge, "artifact", `${path}[${index}]`),
            sha256: requiredSha(edge, "sha256", `${path}[${index}]`),
            verifiedAtRuntime: requiredBoolean(edge, "verifiedAtRunTime", `${path}[${index}]`)
        });
    });
}
function mapUsage(value, path) {
    const usage = requiredRecord(value, path);
    return EngineUsageSchema.parse({
        inputTokens: requiredNonNegativeInteger(usage, "inputTokens", path),
        outputTokens: requiredNonNegativeInteger(usage, "outputTokens", path)
    });
}
function mapMeterPayload(value, path) {
    const meter = requiredRecord(value, path);
    const failuresValue = meter.failures;
    const failures = failuresValue === undefined || failuresValue === null
        ? []
        : requiredArray(meter, "failures", path).map((failure, index) => {
            if (typeof failure === "string" && failure.length > 0)
                return failure;
            return requiredRecord(failure, `${path}.failures[${index}]`);
        });
    return {
        usage: mapUsage(requiredChild(meter, "usage", path), `${path}.usage`),
        completedCalls: requiredNonNegativeInteger(meter, "completedCalls", path),
        failures
    };
}
export function translateProviderMeterArtifact(artifact, context) {
    const mapped = mapMeterPayload(artifact, "provider-meter");
    return EngineProviderMeterSchema.parse({
        kind: "provider-meter",
        status: status(mapped.failures.length > 0 ? "FAIL" : "completed"),
        ...mapped,
        provenance: contextProvenance(context)
    });
}
export function translateTakeoffArtifact(artifact, context) {
    const run = requiredRecord(artifact, "takeoff");
    const output = requiredRecord(requiredChild(run, "output", "takeoff"), "takeoff.output");
    const usage = mapUsage(requiredChild(run, "usage", "takeoff"), "takeoff.usage");
    const gapsValue = requiredChild(output, "gaps", "takeoff.output");
    const gaps = mapGaps(gapsValue, "takeoff.output.gaps");
    const resultStatus = statusFromArtifact(run, context);
    return EngineTakeoffTranslationSchema.parse({
        kind: "takeoff",
        engineRunId: requiredString(run, "run_id", "takeoff"),
        status: resultStatus,
        certificationStatus: certificationStatusFor(resultStatus),
        schemaVersion: requiredString(output, "schemaVersion", "takeoff.output"),
        scope: mapScope(requiredChild(output, "scope", "takeoff.output"), "takeoff.output.scope"),
        gaps,
        reconciliationWarnings: requiredStringArray(output, "reconciliationWarnings", "takeoff.output"),
        run: {
            provider: requiredString(run, "provider", "takeoff"),
            model: requiredString(run, "model", "takeoff"),
            providerCalls: requiredNonNegativeInteger(run, "provider_calls", "takeoff"),
            usage
        },
        provenance: contextProvenance(context)
    });
}
/**
 * Translate the family records emitted by the live mechanical take-off run.
 * These records are intentionally kept separate from the older retained-run
 * shape handled by translateTakeoffArtifact: neither shape is filled from the
 * other, and a missing family record remains missing.
 */
export function translateTakeoffFamilyArtifacts(artifact, context) {
    const run = requiredRecord(artifact, "takeoff");
    const familyRecords = run.family_records;
    if (!Array.isArray(familyRecords))
        return [];
    return familyRecords.flatMap((value, index) => {
        const record = requiredRecord(value, `takeoff.family_records[${index}]`);
        const family = requiredString(record, "family", `takeoff.family_records[${index}]`);
        const pass = requiredRecord(requiredChild(record, "pass", `takeoff.family_records[${index}]`), `takeoff.family_records[${index}].pass`);
        const reconciled = pass.reconciled === undefined || pass.reconciled === null
            ? pass.text
            : pass.reconciled;
        if (reconciled === undefined || reconciled === null)
            return [];
        const resultStatus = statusFromArtifact(run, context);
        return [
            EngineTakeoffFamilyTranslationSchema.parse({
                kind: "takeoff-family",
                family,
                status: resultStatus,
                certificationStatus: certificationStatusFor(resultStatus),
                scope: mapScope(reconciled, `takeoff.family_records[${index}].pass.reconciled`),
                provenance: contextProvenance(context)
            })
        ];
    });
}
export function translateProjectionArtifact(artifact, context) {
    const projection = requiredRecord(artifact, "projection");
    const output = requiredRecord(requiredChild(projection, "output", "projection"), "projection.output");
    const sourceArtifact = mapArtifactReference(requiredChild(projection, "sourceArtifact", "projection"), "projection.sourceArtifact", "source-run");
    // scoreArtifact is deliberately optional: upstream only populates it when a
    // scorecard exists for the run (M23 optional-scorecard law). Its absence is
    // a valid projection result, not a translation defect.
    const scoreArtifact = projection.scoreArtifact === undefined || projection.scoreArtifact === null
        ? undefined
        : mapArtifactReference(projection.scoreArtifact, "projection.scoreArtifact", "scorecard");
    const resultStatus = statusFromArtifact(projection, context);
    return EngineProjectionTranslationSchema.parse({
        kind: "projection",
        status: resultStatus,
        certificationStatus: certificationStatusFor(resultStatus),
        schemaVersion: requiredString(projection, "schemaVersion", "projection"),
        selectedType: requiredString(projection, "selectedType", "projection"),
        scope: mapScope(requiredChild(output, "scope", "projection.output"), "projection.output.scope"),
        gaps: mapGaps(requiredChild(output, "gaps", "projection.output"), "projection.output.gaps"),
        reconciliationWarnings: requiredStringArray(output, "reconciliationWarnings", "projection.output"),
        sourceArtifact,
        scoreArtifact,
        provenance: contextProvenance(context)
    });
}
function mapManifestStage(value, path) {
    const stage = requiredRecord(value, path);
    const mapRefs = (key) => stage[key] === undefined || stage[key] === null
        ? []
        : requiredArray(stage, key, path).map((item, index) => mapArtifactReference(item, `${path}.${key}[${index}]`));
    const usage = stage.usage === undefined || stage.usage === null
        ? undefined
        : mapUsage(stage.usage, `${path}.usage`);
    return EngineManifestStageSchema.parse({
        ordinal: requiredNonNegativeInteger(stage, "ordinal", path),
        stageId: requiredString(stage, "stage", path),
        status: status(requiredString(stage, "status", path)),
        stageRunId: optionalString(stage, "stageRunId", path) ?? undefined,
        provider: optionalString(stage, "provider", path),
        model: optionalString(stage, "model", path),
        usage,
        inputArtifacts: mapRefs("inputs"),
        outputArtifacts: mapRefs("outputs")
    });
}
export function translateChainManifest(artifact, context, options = {}) {
    const manifest = requiredRecord(artifact, "manifest");
    const totals = requiredRecord(requiredChild(manifest, "totals", "manifest"), "manifest.totals");
    const providerMeter = options.providerMeter === undefined
        ? {
            usage: mapUsage(totals, "manifest.totals"),
            completedCalls: undefined,
            failures: [],
            source: "unavailable"
        }
        : {
            ...mapMeterPayload(options.providerMeter, "manifest.provider-meter"),
            source: "stage-owned-provider-meter"
        };
    const stages = requiredArray(manifest, "stages", "manifest").map((stage, index) => mapManifestStage(stage, `manifest.stages[${index}]`));
    return EngineManifestTranslationSchema.parse({
        kind: "chain-manifest",
        pipelineRunId: requiredString(manifest, "pipelineRunId", "manifest"),
        gitSha: requiredString(manifest, "gitSha", "manifest"),
        gitDirty: requiredBoolean(manifest, "gitDirty", "manifest"),
        totalsIncomplete: requiredBoolean(manifest, "totalsIncomplete", "manifest"),
        apartmentType: requiredString(manifest, "apartmentType", "manifest"),
        schemaVersion: requiredString(manifest, "schemaVersion", "manifest"),
        status: deriveManifestStatus(stages),
        chainEdges: mapChainEdges(requiredChild(manifest, "chain", "manifest"), "manifest.chain"),
        stages,
        manifestTotals: {
            inputTokens: requiredNonNegativeInteger(totals, "inputTokens", "manifest.totals"),
            outputTokens: requiredNonNegativeInteger(totals, "outputTokens", "manifest.totals"),
            gbp: requiredNumber(totals, "gbp", "manifest.totals")
        },
        providerMeter,
        provenance: contextProvenance(context)
    });
}
function mapFrequency(value, path) {
    const frequency = requiredRecord(value, path);
    return EnginePointFrequencySchema.parse({
        presentInCorpora: requiredNonNegativeInteger(frequency, "presentInCorpora", path),
        evaluatedCorpora: requiredNonNegativeInteger(frequency, "evaluatedCorpora", path),
        corpora: requiredStringArray(frequency, "corpora", path),
        interpretation: requiredString(frequency, "interpretation", path)
    });
}
function mapConfidence(value, path) {
    if (value === undefined || value === null)
        return undefined;
    const parsed = EngineConfidenceSchema.safeParse(value);
    if (!parsed.success) {
        throw new Error(`Invalid engine artifact confidence at ${path}.`);
    }
    return parsed.data;
}
function mapPoint(value, path) {
    const point = requiredRecord(value, path);
    const frequency = point.frequency === undefined || point.frequency === null
        ? undefined
        : mapFrequency(point.frequency, `${path}.frequency`);
    const load = requiredRecord(requiredChild(point, "load", path), `${path}.load`);
    const pointProvenance = requiredRecord(requiredChild(point, "provenance", path), `${path}.provenance`);
    const provenanceFrequency = pointProvenance.frequency === undefined || pointProvenance.frequency === null
        ? undefined
        : mapFrequency(pointProvenance.frequency, `${path}.provenance.frequency`);
    const confirmationFlags = point.confirmationFlags === undefined || point.confirmationFlags === null
        ? []
        : requiredArray(point, "confirmationFlags", path).map((value, index) => {
            const flag = requiredRecord(value, `${path}.confirmationFlags[${index}]`);
            return {
                id: requiredString(flag, "id", `${path}.confirmationFlags[${index}]`),
                text: requiredString(flag, "text", `${path}.confirmationFlags[${index}]`),
                materiality: requiredString(flag, "materiality", `${path}.confirmationFlags[${index}]`)
            };
        });
    const mapRefs = (key) => requiredStringArray(point, key, path);
    return EnginePointSchema.parse({
        id: requiredString(point, "id", path),
        equipmentId: requiredString(point, "equipmentId", path),
        equipmentKind: requiredString(point, "equipmentKind", path),
        zone: requiredString(point, "zone", path),
        function: requiredString(point, "function", path),
        type: requiredString(point, "type", path),
        state: requiredString(point, "state", path),
        trigger: requiredString(point, "trigger", path),
        contractClass: requiredString(point, "contractClass", path),
        safetyRole: requiredString(point, "safetyRole", path),
        exclusions: requiredStringArray(point, "exclusions", path),
        confidence: mapConfidence(point.confidence, `${path}.confidence`),
        frequency,
        powerSource: requiredString(point, "powerSource", path),
        load: {
            status: requiredString(load, "status", `${path}.load`),
            value: load.value === null ? null : requiredNumber(load, "value", `${path}.load`),
            unit: load.unit === null
                ? null
                : (optionalString(load, "unit", `${path}.load`) ?? null),
            relayRequired: load.relayRequired === null
                ? null
                : (optionalString(load, "relayRequired", `${path}.load`) ?? null)
        },
        requirementRefs: mapRefs("requirementRefs"),
        safetyPatternRefs: mapRefs("safetyPatternRefs"),
        sourceRefs: mapRefs("sourceRefs"),
        reasons: mapRefs("reasons"),
        confirmationFlags,
        needsRich: requiredBoolean(point, "needsRich", path),
        provenance: {
            basis: requiredString(pointProvenance, "basis", `${path}.provenance`),
            requirementRefs: requiredStringArray(pointProvenance, "requirementRefs", `${path}.provenance`),
            sourceRefs: requiredStringArray(pointProvenance, "sourceRefs", `${path}.provenance`),
            assumptionTrigger: optionalString(pointProvenance, "assumptionTrigger", `${path}.provenance`),
            confidence: mapConfidence(pointProvenance.confidence, `${path}.provenance.confidence`),
            frequency: provenanceFrequency
        }
    });
}
function mapNumberRecord(value, path) {
    const record = requiredRecord(value, path);
    return Object.fromEntries(Object.entries(record).map(([key, value]) => {
        if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
            throw new Error(`Invalid engine artifact count at ${path}.${key}.`);
        }
        return [key, value];
    }));
}
function mapPointSchedule(value, path) {
    const schedule = requiredRecord(value, path);
    const totals = requiredRecord(requiredChild(schedule, "totals", path), `${path}.totals`);
    return EnginePointScheduleSchema.parse({
        points: requiredArray(schedule, "points", path).map((point, index) => mapPoint(point, `${path}.points[${index}]`)),
        totals: {
            byType: mapNumberRecord(requiredChild(totals, "byType", `${path}.totals`), `${path}.totals.byType`),
            byState: mapNumberRecord(requiredChild(totals, "byState", `${path}.totals`), `${path}.totals.byState`),
            total: requiredNonNegativeInteger(totals, "total", `${path}.totals`)
        }
    });
}
function mapAllocation(value, path) {
    const allocation = requiredRecord(value, path);
    return {
        pointId: requiredString(allocation, "pointId", path),
        placementId: requiredString(allocation, "placementId", path),
        productCode: requiredString(allocation, "productCode", path),
        capacityKind: optionalString(allocation, "capacityKind", path),
        logicOwnerPlacementId: optionalString(allocation, "logicOwnerPlacementId", path)
    };
}
function mapAllocationSummary(value, path) {
    const allocations = requiredRecord(value, path);
    const mapList = (key) => requiredArray(allocations, key, path).map((item, index) => mapAllocation(item, `${path}.${key}[${index}]`));
    const placements = requiredArray(allocations, "placements", path).map((item, index) => {
        const placement = requiredRecord(item, `${path}.placements[${index}]`);
        return {
            placementId: requiredString(placement, "placementId", `${path}.placements[${index}]`),
            productCode: requiredString(placement, "productCode", `${path}.placements[${index}]`),
            placementClass: requiredString(placement, "placementClass", `${path}.placements[${index}]`),
            purpose: requiredString(placement, "purpose", `${path}.placements[${index}]`),
            location: requiredString(placement, "location", `${path}.placements[${index}]`)
        };
    });
    const pointCoverage = requiredArray(allocations, "pointCoverage", path).map((item, index) => {
        const coverage = requiredRecord(item, `${path}.pointCoverage[${index}]`);
        return {
            pointId: requiredString(coverage, "pointId", `${path}.pointCoverage[${index}]`),
            disposition: requiredString(coverage, "disposition", `${path}.pointCoverage[${index}]`),
            placementId: optionalString(coverage, "placementId", `${path}.pointCoverage[${index}]`),
            note: optionalString(coverage, "note", `${path}.pointCoverage[${index}]`)
        };
    });
    return EngineAllocationSummarySchema.parse({
        deviceAllocations: mapList("deviceAllocations"),
        ioAllocations: mapList("ioAllocations"),
        placements,
        pointCoverage,
        seriesSwitchingAllocations: mapList("seriesSwitchingAllocations")
    });
}
function mapFailuresByPhase(value, path) {
    if (value === undefined || value === null)
        return {};
    const record = requiredRecord(value, path);
    return Object.fromEntries(Object.entries(record).map(([key, value]) => {
        const wrapper = { value };
        return [key, requiredStringArray(wrapper, "value", `${path}.${key}`)];
    }));
}
export function translatePointsArtifact(artifact, context) {
    const run = requiredRecord(artifact, "points");
    const c1 = requiredRecord(requiredChild(run, "c1", "points"), "points.c1");
    const receipt = requiredRecord(requiredChild(c1, "receipt", "points.c1"), "points.c1.receipt");
    const toolReceipts = requiredRecord(requiredChild(receipt, "toolReceipts", "points.c1.receipt"), "points.c1.receipt.toolReceipts");
    let allocationValid;
    let allocationValiditySource;
    if (typeof receipt.allocationValid === "boolean") {
        allocationValid = receipt.allocationValid;
        allocationValiditySource = "receipt.allocationValid";
    }
    else if (typeof c1.allocationValid === "boolean") {
        allocationValid = c1.allocationValid;
        allocationValiditySource = "c1.allocationValid";
    }
    else if (typeof toolReceipts.calculateTotals === "boolean") {
        allocationValid = toolReceipts.calculateTotals;
        allocationValiditySource = "receipt.toolReceipts.calculateTotals";
    }
    else {
        throw new Error("Missing required engine artifact allocation validity at points.c1.receipt.");
    }
    const mapScheduleIfPresent = (key) => run[key] === undefined || run[key] === null
        ? undefined
        : mapPointSchedule(run[key], `points.${key}`);
    const inputArtifactsRecord = requiredRecord(requiredChild(run, "inputArtifacts", "points"), "points.inputArtifacts");
    const inputArtifacts = Object.entries(inputArtifactsRecord).map(([role, value]) => mapArtifactReference(value, `points.inputArtifacts.${role}`, role));
    const finalAllocationsValue = c1.c1bFinal ??
        (() => {
            const c1Final = requiredRecord(requiredChild(c1, "c1Final", "points.c1"), "points.c1.c1Final");
            return requiredChild(c1Final, "allocation", "points.c1.c1Final");
        })();
    const finalAllocations = mapAllocationSummary(finalAllocationsValue, c1.c1bFinal === undefined ? "points.c1.c1Final.allocation" : "points.c1.c1bFinal");
    const resultStatus = statusFromArtifact(c1, context);
    return EnginePointsTranslationSchema.parse({
        kind: "points",
        status: resultStatus,
        certificationStatus: certificationStatusFor(resultStatus),
        sourceRunId: requiredString(run, "sourceRunId", "points"),
        inputMode: requiredString(run, "inputMode", "points"),
        inputArtifacts,
        schedules: {
            base: mapScheduleIfPresent("baseSchedule"),
            afterP2: mapScheduleIfPresent("afterP2"),
            points: mapPointSchedule(requiredChild(run, "pointsSchedule", "points"), "points.pointsSchedule")
        },
        c1: {
            status: status(requiredString(c1, "verdict", "points.c1")),
            receiptStatus: requiredString(receipt, "status", "points.c1.receipt"),
            allocationValid,
            allocationValiditySource,
            toolReceiptsValid: typeof receipt.toolReceiptsValid === "boolean"
                ? receipt.toolReceiptsValid
                : typeof c1.toolReceiptsValid === "boolean"
                    ? c1.toolReceiptsValid
                    : undefined,
            refireExhausted: requiredBoolean(c1, "refireExhausted", "points.c1"),
            refireLimit: requiredNonNegativeInteger(c1, "refireLimit", "points.c1"),
            selectionRefireExhausted: optionalBoolean(c1, "selectionRefireExhausted", "points.c1"),
            allocationRefireCount: Array.isArray(c1.allocationRefires)
                ? c1.allocationRefires.length
                : 0,
            selectionRefireCount: Array.isArray(c1.selectionRefires)
                ? c1.selectionRefires.length
                : 0,
            failures: c1.failures === undefined || c1.failures === null
                ? []
                : requiredStringArray(c1, "failures", "points.c1"),
            failuresByPhase: mapFailuresByPhase(c1.failuresByPhase, "points.c1.failuresByPhase"),
            finalAllocations
        },
        provenance: contextProvenance(context)
    });
}
function mapPanelFit(value, path) {
    const fit = requiredRecord(value, path);
    return EnginePanelFitSchema.parse({
        status: status(requiredString(fit, "status", path)),
        calculationRef: requiredString(fit, "calculationRef", path),
        knownComponentRefs: requiredStringArray(fit, "knownComponentRefs", path),
        knownRequiredDinWidthMm: optionalNumber(fit, "knownRequiredDinWidthMm", path),
        knownMaximumComponentHeightMm: optionalNumber(fit, "knownMaximumComponentHeightMm", path),
        knownMaximumComponentDepthMm: optionalNumber(fit, "knownMaximumComponentDepthMm", path),
        reason: requiredString(fit, "reason", path)
    });
}
function mapPanelSpareCapacity(value, path) {
    const spare = requiredRecord(value, path);
    return EnginePanelSpareCapacitySchema.parse({
        status: status(requiredString(spare, "status", path)),
        requiredPercent: requiredNumber(spare, "requiredPercent", path),
        actualPercent: spare.actualPercent === undefined || spare.actualPercent === null
            ? null
            : requiredNumber(spare, "actualPercent", path),
        policyProfileRef: requiredString(spare, "policyProfileRef", path),
        calculationRef: requiredString(spare, "calculationRef", path),
        reasoning: requiredString(spare, "reasoning", path)
    });
}
function mapPanelPsuCapacity(value, path) {
    const psu = requiredRecord(value, path);
    return EnginePanelPsuCapacitySchema.parse({
        status: status(requiredString(psu, "status", path)),
        calculationRef: requiredString(psu, "calculationRef", path),
        unit: requiredString(psu, "unit", path),
        totalCapacity: requiredNumber(psu, "totalCapacity", path),
        totalDraw: requiredNumber(psu, "totalDraw", path),
        requiredCapacity: requiredNumber(psu, "requiredCapacity", path),
        reservePercent: requiredNumber(psu, "reservePercent", path),
        overloaded: psu.overloaded === undefined || psu.overloaded === null
            ? null
            : requiredBoolean(psu, "overloaded", path),
        missingConsumerRefs: requiredStringArray(psu, "missingConsumerRefs", path),
        excludedConsumerRefs: requiredStringArray(psu, "excludedConsumerRefs", path),
        assumptionComponentRefs: requiredStringArray(psu, "assumptionComponentRefs", path),
        reasoning: requiredString(psu, "reasoning", path)
    });
}
function mapPanelRelayLoading(value, path) {
    const relay = requiredRecord(value, path);
    return EnginePanelRelayLoadingSchema.parse({
        status: status(requiredString(relay, "status", path)),
        calculationRef: requiredString(relay, "calculationRef", path),
        totalConnectedLoadA: requiredNumber(relay, "totalConnectedLoadA", path),
        overloaded: relay.overloaded === undefined || relay.overloaded === null
            ? null
            : requiredBoolean(relay, "overloaded", path),
        unknownLoadPointIds: requiredStringArray(relay, "unknownLoadPointIds", path),
        assumedLoadPointIds: requiredStringArray(relay, "assumedLoadPointIds", path)
    });
}
function mapPanelComponent(value, path) {
    const component = requiredRecord(value, path);
    const componentId = ["componentId", "relayId"]
        .map((key) => optionalString(component, key, path))
        .find((candidate) => candidate !== undefined);
    if (!componentId) {
        throw new Error(`Missing required engine artifact component id at ${path}.`);
    }
    return {
        componentId,
        sku: requiredString(component, "sku", path),
        quantity: requiredNonNegativeInteger(component, "quantity", path),
        reasoning: requiredString(component, "reasoning", path),
        sourceRefs: requiredStringArray(component, "sourceRefs", path)
    };
}
function mapPanelComponentList(value, path) {
    if (value === undefined || value === null)
        return [];
    return requiredArray({ value }, "value", path).map((item, index) => mapPanelComponent(item, `${path}[${index}]`));
}
function mapPanel(value, path) {
    const panel = requiredRecord(value, path);
    return EnginePanelSchema.parse({
        panelId: requiredString(panel, "panelId", path),
        kind: requiredString(panel, "kind", path),
        location: requiredString(panel, "location", path),
        fit: mapPanelFit(requiredChild(panel, "fit", path), `${path}.fit`),
        spareCapacity: mapPanelSpareCapacity(requiredChild(panel, "spareCapacity", path), `${path}.spareCapacity`),
        psuCapacity: panel.psuCapacity === undefined || panel.psuCapacity === null
            ? undefined
            : mapPanelPsuCapacity(panel.psuCapacity, `${path}.psuCapacity`),
        relayLoading: panel.relayLoading === undefined || panel.relayLoading === null
            ? undefined
            : mapPanelRelayLoading(panel.relayLoading, `${path}.relayLoading`),
        controllers: mapPanelComponentList(panel.controllers, `${path}.controllers`),
        ioModules: mapPanelComponentList(panel.ioModules, `${path}.ioModules`),
        netmasters: mapPanelComponentList(panel.netmasters, `${path}.netmasters`),
        screens: mapPanelComponentList(panel.screens, `${path}.screens`),
        psus: mapPanelComponentList(panel.psus, `${path}.psus`),
        relays: mapPanelComponentList(panel.relays, `${path}.relays`)
    });
}
export function translatePanelArtifact(artifact, context) {
    const panel = requiredRecord(artifact, "panel");
    const catalogGaps = requiredArray(panel, "catalogGaps", "panel").map((value, index) => {
        const gap = requiredRecord(value, `panel.catalogGaps[${index}]`);
        return {
            gapId: requiredString(gap, "gapId", `panel.catalogGaps[${index}]`),
            sku: requiredString(gap, "sku", `panel.catalogGaps[${index}]`),
            reason: requiredString(gap, "reason", `panel.catalogGaps[${index}]`),
            requiredProvision: requiredString(gap, "requiredProvision", `panel.catalogGaps[${index}]`)
        };
    });
    const feedbackFlags = requiredArray(panel, "feedbackFlags", "panel").map((value, index) => {
        const flag = requiredRecord(value, `panel.feedbackFlags[${index}]`);
        return {
            flagId: requiredString(flag, "flagId", `panel.feedbackFlags[${index}]`),
            kind: requiredString(flag, "kind", `panel.feedbackFlags[${index}]`),
            status: requiredString(flag, "status", `panel.feedbackFlags[${index}]`),
            statement: requiredString(flag, "statement", `panel.feedbackFlags[${index}]`),
            affectedRefs: requiredStringArray(flag, "affectedRefs", `panel.feedbackFlags[${index}]`),
            basis: requiredString(flag, "basis", `panel.feedbackFlags[${index}]`),
            nextAction: requiredString(flag, "nextAction", `panel.feedbackFlags[${index}]`)
        };
    });
    const unknowns = panel.unknowns === undefined || panel.unknowns === null
        ? []
        : requiredArray(panel, "unknowns", "panel").map((value, index) => {
            const unknown = requiredRecord(value, `panel.unknowns[${index}]`);
            return {
                unknownId: requiredString(unknown, "unknownId", `panel.unknowns[${index}]`),
                category: requiredString(unknown, "category", `panel.unknowns[${index}]`),
                statement: requiredString(unknown, "statement", `panel.unknowns[${index}]`),
                downstreamEffect: requiredString(unknown, "downstreamEffect", `panel.unknowns[${index}]`),
                affectedRefs: requiredStringArray(unknown, "affectedRefs", `panel.unknowns[${index}]`),
                sourceRefs: requiredStringArray(unknown, "sourceRefs", `panel.unknowns[${index}]`)
            };
        });
    return EnginePanelTranslationSchema.parse({
        kind: "panel",
        status: status(requiredString(panel, "status", "panel")),
        artifactKind: requiredString(panel, "artifactKind", "panel"),
        schemaVersion: requiredString(panel, "schemaVersion", "panel"),
        catalogGaps,
        feedbackFlags,
        unknowns,
        panels: requiredArray(panel, "panels", "panel").map((value, index) => mapPanel(value, `panel.panels[${index}]`)),
        provenance: contextProvenance(context)
    });
}
function mapScoreSummary(value, path) {
    const summary = requiredRecord(value, path);
    return EngineScoreSummarySchema.parse({
        correct: requiredNonNegativeInteger(summary, "correct", path),
        wrong: requiredNonNegativeInteger(summary, "wrong", path),
        missing: requiredNonNegativeInteger(summary, "missing", path),
        fabricated: requiredNonNegativeInteger(summary, "fabricated", path),
        truthGap: requiredNonNegativeInteger(summary, "truthGap", path),
        scored: requiredNonNegativeInteger(summary, "scored", path),
        total: requiredNonNegativeInteger(summary, "total", path)
    });
}
function mapFamilyScores(value, path) {
    if (value === undefined || value === null)
        return [];
    return requiredArray({ value }, "value", path).map((item, index) => {
        const family = requiredRecord(item, `${path}[${index}]`);
        return {
            family: requiredString(family, "family", `${path}[${index}]`),
            text: mapScoreSummary(requiredRecord(requiredChild(family, "text", `${path}[${index}]`), `${path}[${index}].text`).summary, `${path}[${index}].text.summary`),
            vision: mapScoreSummary(requiredRecord(requiredChild(family, "vision", `${path}[${index}]`), `${path}[${index}].vision`).summary, `${path}[${index}].vision.summary`),
            reconciled: mapScoreSummary(requiredRecord(requiredChild(family, "reconciled", `${path}[${index}]`), `${path}[${index}].reconciled`).summary, `${path}[${index}].reconciled.summary`)
        };
    });
}
function mapFutureTruth(value, path) {
    const fields = requiredRecord(value, path);
    return Object.entries(fields).map(([field, value]) => {
        const record = requiredRecord(value, `${path}.${field}`);
        const provenance = requiredRecord(requiredChild(record, "provenance", `${path}.${field}`), `${path}.${field}.provenance`);
        return {
            field,
            value: requiredNumber(record, "value", `${path}.${field}`),
            provenance: {
                sheet: requiredString(provenance, "sheet", `${path}.${field}.provenance`),
                row: requiredNonNegativeInteger(provenance, "row", `${path}.${field}.provenance`),
                columns: requiredStringArray(provenance, "columns", `${path}.${field}.provenance`),
                method: requiredString(provenance, "method", `${path}.${field}.provenance`),
                formula: provenance.formula === undefined || provenance.formula === null
                    ? null
                    : requiredString(provenance, "formula", `${path}.${field}.provenance`)
            }
        };
    });
}
export function translateScorecardArtifact(artifact, context) {
    const scorecard = requiredRecord(artifact, "scorecard");
    const reconciled = requiredRecord(requiredChild(scorecard, "reconciled", "scorecard"), "scorecard.reconciled");
    const futureTruth = requiredRecord(requiredChild(scorecard, "future_specification_truth", "scorecard"), "scorecard.future_specification_truth");
    return EngineScorecardTranslationSchema.parse({
        kind: "scorecard",
        engineRunId: requiredString(scorecard, "run_id", "scorecard"),
        status: status("unknown"),
        provider: requiredString(scorecard, "provider", "scorecard"),
        model: requiredString(scorecard, "model", "scorecard"),
        homeTypeId: requiredString(scorecard, "home_type_id", "scorecard"),
        truthClass: requiredString(requiredRecord(requiredChild(scorecard, "truth", "scorecard"), "scorecard.truth"), "truthClass", "scorecard.truth"),
        reconciled: mapScoreSummary(requiredChild(reconciled, "summary", "scorecard.reconciled"), "scorecard.reconciled.summary"),
        familyScores: mapFamilyScores(scorecard.family_scores, "scorecard.family_scores"),
        futureTruth: mapFutureTruth(requiredChild(futureTruth, "fields", "scorecard.future_specification_truth"), "scorecard.future_specification_truth.fields"),
        reconciliationWarnings: futureTruth.reconciliationWarnings === undefined ||
            futureTruth.reconciliationWarnings === null
            ? []
            : requiredStringArray(futureTruth, "reconciliationWarnings", "scorecard.future_specification_truth"),
        provenance: contextProvenance(context)
    });
}
//# sourceMappingURL=engineArtifactAdapter.js.map